import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { FournisseurService } from "../../services/fournisseurs/fournisseurService";
import { DashboardCharts } from "../../components/analytics/DashboardCharts"; // Reusing charts if suitable
import { 
  TrendingUp, 
  FileText, 
  CreditCard, 
  AlertTriangle
} from "lucide-react";
import { toast } from "react-hot-toast";
import { getUserDisplayName } from "../../utils/userUtils";
import { useTranslation } from "react-i18next";

export const FournisseurDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const fournisseurId = user?.fournisseur_profile_id;
      if (!fournisseurId) { setLoading(false); return; }
      try {
        const response = await FournisseurService.getFournisseurStats(fournisseurId);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats", error);
        toast.error(t('common.errorGroup'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  const fmt = (v: number) =>
    new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', {
      notation: "compact", compactDisplay: "short", style: "currency", currency: "XOF",
    }).format(v);

  const dashboardStats = [
    {
      name: t('dashboard.fournisseur.revenue'),
      value: stats?.volume_total != null ? fmt(stats.volume_total) : "0 FCFA",
      change: stats?.volume_mensuel != null ? fmt(stats.volume_mensuel) + " ce mois" : "",
      icon: TrendingUp,
      color: "green",
    },
    {
      name: t('dashboard.client.pendingInvoices'),
      value: stats?.factures_soumises ?? 0,
      change: `${stats?.factures_totales ?? 0} total`,
      icon: FileText,
      color: "blue",
    },
    {
      name: t('dashboard.fournisseur.paidPayments'),
      value: stats?.factures_payees ?? 0,
      change: stats?.montant_paye != null ? fmt(stats.montant_paye) : "0 FCFA",
      icon: CreditCard,
      color: "purple",
    },
    {
      name: t('dashboard.fournisseur.paymentDelays'),
      value: stats?.delai_moyen_paiement != null ? `${stats.delai_moyen_paiement}j` : "—",
      change: `${stats?.taux_performance ?? 0}% validation`,
      icon: AlertTriangle,
      color: "red",
    },
  ];

  if (loading) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboard.fournisseur.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {t('header.welcome')}, {getUserDisplayName(user)}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {dashboardStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                      <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd>
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                        <div className="text-sm font-medium text-gray-500">
                          {stat.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts or Recent Activity */}
      <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {t('dashboard.fournisseur.activityOverview')}
          </h3>
          <DashboardCharts /> 
      </div>
    </div>
  );
};
